if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }


      connectedCallback() {
        this.colorProduct = this.closest('product-info');
        this.colorChangeHandler = (event) => {
          if (!event.target.closest('variant-selects')) return;
          cancelAnimationFrame(this.colorFrame);
          this.colorFrame = requestAnimationFrame(() => this.refreshColorGallery());
        };
        this.colorProduct?.addEventListener('change', this.colorChangeHandler);
        this.colorFrame = requestAnimationFrame(() => this.refreshColorGallery());
      }

      disconnectedCallback() {
        this.colorProduct?.removeEventListener('change', this.colorChangeHandler);
        cancelAnimationFrame(this.colorFrame);
      }

      normalizeColor(value) {
        return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      }

      filterByColor() {
        const picker = this.colorProduct?.querySelector('variant-selects');
        const colorInput = picker?.querySelector('input[data-option-name="Color"]:checked, input[name^="Color-"]:checked, select[name="options[Color]"]');
        const color = this.normalizeColor(colorInput?.value);
        const items = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const colors = Array.from(picker?.querySelectorAll('input[data-option-name="Color"], input[name^="Color-"], select[name="options[Color]"] option') || []).map((input) => this.normalizeColor(input.value));
        const matchingColor = items.filter((item) => color && this.normalizeColor(item.querySelector('img')?.alt).includes(color));
        const singleColor = matchingColor.filter((item) => {
          const alt = this.normalizeColor(item.querySelector('img')?.alt);
          return !colors.some((other) => other && other !== color && alt.includes(other));
        });
        const matches = singleColor.length ? singleColor : matchingColor;
        const allowed = new Set((matches.length ? matches : items).map((item) => item.dataset.mediaId));
        const toggle = (item, visible) => {
          if (!visible) {
            item.setAttribute('data-bonito-color-hidden', '');
            item.style.setProperty('display', 'none', 'important');
          } else if (item.hasAttribute('data-bonito-color-hidden')) {
            item.removeAttribute('data-bonito-color-hidden');
            item.style.removeProperty('display');
          }
        };
        items.forEach((item) => toggle(item, allowed.has(item.dataset.mediaId)));
        let position = 0;
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((item) => {
          const visible = allowed.has(item.dataset.target);
          toggle(item, visible);
          if (visible) {
            item.dataset.mediaPosition = String(++position);
            const button = item.querySelector('button');
            const label = button?.getAttribute('aria-label');
            if (label) button.setAttribute('aria-label', label.replace(/\d+/, String(position)));
          }
        });
        const sectionId = this.id.replace('MediaGallery-', '');
        const modal = document.getElementById('ProductModal-' + sectionId);
        modal?.querySelectorAll('.product-media-modal__content > [data-media-id]').forEach((item) => {
          toggle(item, allowed.has(sectionId + '-' + item.dataset.mediaId));
        });
        [this.elements.viewer, this.elements.thumbnails].forEach((slider) => {
          if (!slider?.slider) return;
          slider.resetPages?.();
          if (slider.sliderItemsToShow?.length === 1) {
            slider.currentPage = 1;
            slider.totalPages = 1;
            slider.sliderItemOffset = slider.slider.clientWidth || 1;
            if (slider.currentPageElement) slider.currentPageElement.textContent = '1';
            if (slider.pageTotalElement) slider.pageTotalElement.textContent = '1';
            slider.prevButton?.setAttribute('disabled', 'disabled');
            slider.nextButton?.setAttribute('disabled', 'disabled');
          }
        });
        return items.filter((item) => allowed.has(item.dataset.mediaId));
      }

      refreshColorGallery() {
        const items = this.filterByColor();
        const active = items.find((item) => item.classList.contains('is-active')) || items[0];
        if (active) this.setActiveMedia(active.dataset.mediaId, false);
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const allowedMedia = this.filterByColor();
        const activeMedia = allowedMedia.find((item) => item.dataset.mediaId === mediaId) || allowedMedia[0];
        mediaId = activeMedia?.dataset.mediaId;
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
